/**
 * Toasts.
 *
 * Behaviour copied from [Sonner](https://sonner.emilkowal.ski/), which became
 * the default once shadcn/ui adopted it; the accessibility contract from
 * [Radix Toast](https://www.radix-ui.com/primitives/docs/components/toast).
 * Neither is installed — both are React and this editor is vanilla. The
 * contract is worth a hundred lines; the dependency is not.
 *
 * **The live region exists from boot, before there is anything to say.** A
 * region created in the same tick as its first message is frequently never
 * announced — a real shipped bug in Radix (radix-ui/primitives#3634). "Has a
 * live region" and "announces" are different claims.
 *
 * What may be a toast is a closed list of five, in
 * docs/decisions/mode-clarity-and-toasts.md. The rule behind it: a toast
 * reports an **event that is already over**. State — which tool, which layer,
 * whether the work is saved — lives on screen permanently, because a question
 * asked at an arbitrary moment cannot be answered by something that was true
 * four seconds ago.
 */

/**
 * What kind of thing happened.
 *
 * Tone drives the icon and the accent, and **nothing else** — not the lifetime,
 * not the position, not whether it can be dismissed. A refusal that behaved
 * differently from a confirmation would be a second component wearing the same
 * name.
 */
export type ToastTone = "info" | "success" | "warn" | "danger";

export interface ToastOptions {
  /**
   * Milliseconds. Defaults follow Material's 2–6s: 4s to read a reason, 6s
   * when there is an action, because a keyboard user has to be able to reach
   * it before it leaves.
   */
  duration?: number;
  /** Adds a button, which makes this a snackbar and lengthens the default. */
  action?: { label: string; onSelect: () => void };
  tone?: ToastTone;
  /** Raw SVG, overriding the tone's default. */
  icon?: string;
}

const DEFAULT_MS = 4000;
const WITH_ACTION_MS = 6000;
/**
 * Beyond this the oldest leaves, rather than a wall of toasts forming.
 *
 * It leaves the same way it would have on its own timer — animated, not
 * yanked — or the stack flickers every time a fourth arrives.
 */
const MAX_VISIBLE = 3;

/*
  Inlined rather than added to icons.json, matching what panel.ts already does
  for its row glyphs. The manifest is shared with the other half of the app and
  a toast icon is not a design-system concern.

  All four are 24px stroke icons on the same grid, so they sit identically.
*/
const ICONS: Record<ToastTone, string> = {
  info: '<circle cx="12" cy="12" r="9"/><path d="M12 16v-4M12 8h.01"/>',
  success: '<circle cx="12" cy="12" r="9"/><path d="m8.5 12.5 2.5 2.5 4.5-5"/>',
  warn: '<path d="M10.3 4.5 2.8 17a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3l-7.5-12.5a2 2 0 0 0-3.4 0Z"/><path d="M12 9v4M12 17h.01"/>',
  danger: '<circle cx="12" cy="12" r="9"/><path d="m15 9-6 6M9 9l6 6"/>',
};

function iconSvg(markup: string): string {
  return `<svg viewBox="0 0 24 24" class="toast-icon" fill="none" stroke="currentColor"
    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${markup}</svg>`;
}

let host: HTMLElement | null = null;

function ensureHost(): HTMLElement {
  if (host?.isConnected) return host;
  const el = document.createElement("ol");
  el.className = "toasts";
  /*
    `status` + polite, not `alert` + assertive. Every toast here results from
    something the user just did, so none of it is news urgent enough to
    interrupt what a screen reader is already saying.
  */
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.setAttribute("aria-relevant", "additions");
  document.body.appendChild(el);
  host = el;
  return el;
}

/** Mount the live region early, so the first message is actually announced. */
export function installToaster(): void {
  ensureHost();
}

export function toast(message: string, opts: ToastOptions = {}): () => void {
  const parent = ensureHost();
  const tone = opts.tone ?? "info";
  const duration = opts.duration ?? (opts.action ? WITH_ACTION_MS : DEFAULT_MS);

  const item = document.createElement("li");
  item.className = "toast";
  item.dataset.tone = tone;

  const icon = document.createElement("span");
  icon.className = "toast-glyph";
  icon.innerHTML = iconSvg(opts.icon ?? ICONS[tone]);
  item.appendChild(icon);

  const text = document.createElement("p");
  text.className = "toast-text";
  // textContent, not innerHTML: every caller passes a layer name eventually,
  // and a layer name is whatever the user typed.
  text.textContent = message;
  item.appendChild(text);

  if (opts.action) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "toast-action";
    button.textContent = opts.action.label;
    button.addEventListener("click", () => {
      opts.action?.onSelect();
      dismiss();
    });
    item.appendChild(button);
  }

  const close = document.createElement("button");
  close.type = "button";
  close.className = "toast-close";
  close.setAttribute("aria-label", "Dismiss");
  close.innerHTML = iconSvg('<path d="M18 6 6 18M6 6l12 12"/>');
  close.addEventListener("click", () => dismiss());
  item.appendChild(close);

  let timer = 0;
  let done = false;

  function dismiss() {
    if (done) return;
    done = true;
    window.clearTimeout(timer);
    item.dataset.leaving = "";
    /*
      Removed when the leave animation ends, with a timer as the backstop.
      `animationend` never fires when the animation is suppressed — reduced
      motion, or a background tab — and without the backstop the node would
      stay in the live region forever, silently accumulating.
    */
    const drop = () => item.remove();
    item.addEventListener("animationend", drop, { once: true });
    window.setTimeout(drop, 400);
  }

  function arm() {
    window.clearTimeout(timer);
    timer = window.setTimeout(dismiss, duration);
  }

  /*
    Pause while a pointer or the keyboard is on it. Sonner does this and it is
    not a flourish: a toast carrying an action that expires while you reach for
    it is worse than one that never appeared.
  */
  item.addEventListener("pointerenter", () => window.clearTimeout(timer));
  item.addEventListener("pointerleave", arm);
  item.addEventListener("focusin", () => window.clearTimeout(timer));
  item.addEventListener("focusout", arm);

  // Newest last, so reading order and visual order agree.
  parent.appendChild(item);

  // Retire the overflow through the same leave animation the timer uses.
  const live = [...parent.children].filter((c) => !(c as HTMLElement).dataset.leaving);
  for (const stale of live.slice(0, Math.max(0, live.length - MAX_VISIBLE))) {
    (stale as HTMLElement).dispatchEvent(new CustomEvent("toast:retire"));
  }

  item.addEventListener("toast:retire", () => dismiss());
  arm();

  return dismiss;
}
